import { Modal, useModals } from 'almanac-ui/Modal'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { StatusPill, StatusPillProps } from 'screens/Calendar/WeekViewV2/CalendarEventCard/StatusPill'
import Styles from './ManageScheduledPostTargetsModal.module.scss'
import { Section } from './Section/Section'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/useScheduledPostTargets'
import { ScheduledPostTargetRow } from './ScheduledPostTargetRow/ScheduledPostTargetRow'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import {
  useCreateScheduledPostTarget,
  createScheduledPostTarget,
} from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useState, useCallback } from 'react'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useCreateCalendarItem } from 'hooks/queries/calendar/useCreateCalendarItem'
import { useUpdateCalendarItem } from 'hooks/queries/calendar/useUpdateCalendarItem'
import { useDeleteCalendarItem } from 'hooks/queries/calendar/useDeleteCalendarItem'
import { Button } from 'blaze-ui/Button'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { CalendarTrash, ChevronDown } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { useQueryClient, useMutation } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { updateCalendarItemInCache } from 'hooks/queries/calendar/utils'
import { CalendarFilters } from 'screens/Calendar/types'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { useCalendarItem } from 'screens/Calendar/FullScreenPreview/useCalendarItem'
import { STATUS_COLOR } from 'screens/ScheduledPosts/Filters/StatusMenu'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { validateScheduledPost } from 'utils/validateScheduledPost'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { scheduledPostTargetsKeys } from 'hooks/queries/scheduledPostTargets/scheduledPostTargetsKeys'

const NEW_STATUS_MAP: Record<PostStatusPropertyValue, StatusPillProps> = {
  [PostStatusPropertyValue.AutoPostScheduled]: {
    status: PostStatusPropertyValue.AutoPostScheduled,
    color: STATUS_COLOR[PostStatusPropertyValue.AutoPostScheduled],
    fontColor: '#220018',
    testId: 'scheduled',
  },
  [PostStatusPropertyValue.Draft]: {
    status: PostStatusPropertyValue.Draft,
    color: STATUS_COLOR[PostStatusPropertyValue.Draft],
    fontColor: '#262B35',
    testId: 'not-scheduled',
  },
  [PostStatusPropertyValue.Posted]: {
    status: PostStatusPropertyValue.Posted,
    color: STATUS_COLOR[PostStatusPropertyValue.Posted],
    fontColor: '#FFFFFF',
    testId: 'posted',
  },
  [PostStatusPropertyValue.Failed]: {
    status: PostStatusPropertyValue.Failed,
    color: STATUS_COLOR[PostStatusPropertyValue.Failed],
    fontColor: '#FFFFFF',
    testId: 'failed',
  },
  // DEPRECATED
  [PostStatusPropertyValue.ReminderScheduled]: {
    status: PostStatusPropertyValue.ReminderScheduled,
    color: STATUS_COLOR[PostStatusPropertyValue.ReminderScheduled],
    fontColor: '#220018',
    testId: 'reminder-set',
  },
}

export const ManageScheduledPostTargetsModal = ({
  almanacFile,
  crossPostGroup,
  showVideoCrossPost = false,
  filters = {},
  onUpdateAlmanacFile,
}: {
  almanacFile: AlmanacFileShowItem
  crossPostGroup: readonly ExternalIntegrationEnum[]
  showVideoCrossPost: boolean
  filters?: CalendarFilters
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
}) => {
  const { closeModal } = useModals()
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const overlayState = useOverlayTriggerState({})
  const [calendarPickerPostAt, setCalendarPickerPostAt] = useState<string | null>(almanacFile.post_at || null)

  const { data: targets = [] } = useScheduledPostTargets(almanacFile.scheduled_post.id, {
    enablePolling: true,
  })

  const { data: scheduledPost } = useScheduledPost({ id: almanacFile.scheduled_post.id })
  const { data: externalIntegrations } = useExternalIntegrations()
  const createMutation = useCreateScheduledPostTarget()
  const deleteMutation = useDeleteScheduledPostTarget()
  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets()
  const openWindowSafely = useOpenWindowSafely()

  // Custom mutation for create-and-publish that doesn't show the "enabled" toast
  const createAndPublishMutation = useMutation(
    async (variables: { scheduled_post_id: number; provider: ExternalIntegrationEnum; target_overrides: any }) => {
      return createScheduledPostTarget(variables, workspaceId)
    },
    {
      onMutate: async (newTarget) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id))

        // Snapshot the previous value
        const previousTargets = queryClient.getQueryData<ScheduledPostTarget[]>(
          scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
        )

        // Optimistically update with "pending" status initially
        const optimisticTarget: ScheduledPostTarget = {
          id: -1,
          status: 'pending', // Use pending initially, publish mutation will update to processing
          target_overrides: newTarget.target_overrides || {},
          scheduled_post_id: newTarget.scheduled_post_id,
          external_integration_id: 0,
          provider: newTarget.provider,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }

        queryClient.setQueryData(
          scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
          (old: ScheduledPostTarget[] = []) => [...old, optimisticTarget],
        )

        // No toast message - we want seamless operation

        return { previousTargets }
      },
      onSuccess: (newTarget) => {
        // Immediately publish the new target
        publishScheduledPostTargets({
          scheduledPostId: almanacFile.scheduled_post.id,
          targetIds: [newTarget.id],
        })
      },
      onError: (err, newTarget, context) => {
        // Rollback on error
        if (context?.previousTargets !== undefined) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
            context.previousTargets,
          )
        }
      },
      onSettled: (data, error, variables) => {
        // Always refetch after error or success
        queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduled_post_id))
      },
    },
  )

  // Calendar item hooks
  const createCalendarItemMutation = useCreateCalendarItem(almanacFile.parent_id || '')
  const updateCalendarItemMutation = useUpdateCalendarItem(almanacFile.parent_id || '')
  const deleteCalendarItemMutation = useDeleteCalendarItem(almanacFile.parent_id || '')

  const postedTargets = targets.filter((target) => target.status === 'published')
  const failedTargets = targets.filter((target) => target.status === 'failed')

  const getTargetForPlatform = (platform: ExternalIntegrationEnum): ScheduledPostTarget | undefined => {
    return targets.find((target) => target.provider === platform)
  }

  const getAccountForTarget = (target: ScheduledPostTarget) => {
    return externalIntegrations?.external_integrations.find(
      (integration) => integration.id === target.external_integration_id,
    )
  }

  const validatePost = (platform: ExternalIntegrationEnum) => {
    const target = getTargetForPlatform(platform)
    return validateScheduledPost(scheduledPost, platform, target)
  }

  const handlePublish = (targetId: number) => {
    publishScheduledPostTargets({ scheduledPostId: almanacFile.scheduled_post.id, targetIds: [targetId] })
  }

  const handlePublishWithoutTarget = (platform: ExternalIntegrationEnum) => {
    createAndPublishMutation.mutate({
      scheduled_post_id: almanacFile.scheduled_post.id,
      provider: platform,
      target_overrides: {},
    })
  }

  const handleViewPost = (url: string) => {
    openWindowSafely(url)
  }

  const handleRetry = (targetId: number) => {
    publishScheduledPostTargets({ scheduledPostId: almanacFile.scheduled_post.id, targetIds: [targetId] })
  }

  // Subscribe to the authoritative calendar item detail cache so UI reflects saved updates
  const { data: detailData } = useCalendarItem(
    { id: almanacFile.key, filters, startDate: almanacFile.post_at || '' },
    { enabled: true },
  )
  const currentAlmanacFile = (detailData?.almanac_file as AlmanacFileCalendarItem) || almanacFile

  const hasIntegration = !!currentAlmanacFile?.external_integration

  const status = (currentAlmanacFile.status as PostStatusPropertyValue) || PostStatusPropertyValue.Draft
  const statusPillProps = NEW_STATUS_MAP[status]

  const updateCalendarItemCache = useCallback(
    (date: Date | null, value: PostStatusPropertyValue) => {
      // Update all calendar caches (including WeekViewV2)
      updateCalendarItemInCache(
        queryClient,
        almanacFile.key,
        (item) => ({
          ...item,
          post_at: date?.toISOString() || '',
          status: value,
        }),
        workspaceId,
      )

      // Also update the detail cache for the full screen preview
      const queryKey = calendarKeys.detailWithFilters(almanacFile.key, filters)
      const queryData = queryClient.getQueryData(queryKey)

      if (queryData && typeof queryData === 'object' && 'almanac_file' in queryData) {
        const updatedAlmanacFile = {
          ...(queryData.almanac_file as AlmanacFileCalendarItem),
          post_at: date?.toISOString() || '',
          status: value,
        }

        const newData = {
          ...queryData,
          almanac_file: updatedAlmanacFile,
        }

        queryClient.setQueryData(queryKey, newData)

        // Also update the local state if callback is provided
        if (onUpdateAlmanacFile) {
          // Update with the original almanacFile object to maintain all ShowItem properties
          onUpdateAlmanacFile({
            ...almanacFile,
            post_at: date?.toISOString() || '',
            post_status: value,
          })
        }
      }
    },
    [filters, queryClient, onUpdateAlmanacFile, almanacFile, workspaceId],
  )

  const handleSchedule = async (date: Date, value: PostStatusPropertyValue) => {
    if (!almanacFile) return

    setIsLoading(true)

    try {
      const formattedDate = dayjs(date).format('ddd, MMM DD, h:mm A')
      if (currentAlmanacFile.status === PostStatusPropertyValue.AutoPostScheduled) {
        // Update existing calendar item
        const updatedItem = {
          ...currentAlmanacFile,
          post_at: date.toISOString(),
          status: value,
        }
        await updateCalendarItemMutation.mutateAsync({
          updatedItem,
          originalPostAt: currentAlmanacFile.post_at,
        })
        showToastMessage(`Post Updated For ${formattedDate}`, 'success')
      } else {
        // Create new calendar item
        await createCalendarItemMutation.mutateAsync({
          id: currentAlmanacFile.key,
          postAt: date.toISOString(),
          status: value,
          unscheduledItem: currentAlmanacFile,
        })
        showToastMessage(`Post Scheduled For ${formattedDate}`, 'success')
      }

      // Update the detail cache
      updateCalendarItemCache(date, value)

      // Invalidate all calendar queries to ensure WeekViewV2 updates
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(calendarKeys.filters(filters))
      queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))

      // Force refetch the detail query to ensure fresh data
      const detailQueryKey = calendarKeys.detailWithFilters(currentAlmanacFile.key, filters)
      queryClient.refetchQueries(detailQueryKey)

      // Update the picker state to reflect the new date
      setCalendarPickerPostAt(date.toISOString())
    } catch (error) {
      showToastMessage(`Failed to schedule post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnschedule = async () => {
    setIsLoading(true)

    try {
      await deleteCalendarItemMutation.mutateAsync(currentAlmanacFile)
      showToastMessage(`Post Unscheduled.  Go to Recents to reschedule`, 'success')

      // Invalidate all calendar queries to ensure WeekViewV2 updates
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(calendarKeys.filters(filters))
      queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))

      // Clear the picker state since the post is no longer scheduled
      setCalendarPickerPostAt(null)

      // Update cache to reflect unscheduled state
      updateCalendarItemCache(null, PostStatusPropertyValue.Draft)
    } catch (error) {
      showToastMessage(`Failed to unschedule post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggle = (platform: ExternalIntegrationEnum, isChecked: boolean) => {
    if (isChecked) {
      createMutation.mutate({
        scheduled_post_id: almanacFile.scheduled_post.id,
        provider: platform,
        target_overrides: {},
      })
    } else {
      const target = getTargetForPlatform(platform)
      if (target) {
        deleteMutation.mutate({
          scheduled_post_id: almanacFile.scheduled_post.id,
          target_id: target.id,
        })
      }
    }
  }

  return (
    <Modal.Root size="md" onClose={() => closeModal()} onPressOutside={() => closeModal()}>
      <Modal.Header
        title="Manage Cross Posts"
        actions={
          <div className={Styles.actions}>
            <StatusPill {...statusPillProps} />
            {status !== PostStatusPropertyValue.Posted && (
              <Tooltip label="Enable at least one post to start scheduling" hide={!!targets?.length}>
                <div>
                  <DateTimePicker
                    onClose={overlayState.close}
                    dateTime={calendarPickerPostAt ? dayjs(calendarPickerPostAt) : undefined}
                    actions={(close) => (
                      <div className={Styles.actions}>
                        {currentAlmanacFile?.post_at && (
                          <Button
                            variant="tertiary"
                            frontIcon={CalendarTrash}
                            fullWidth
                            size="lg"
                            isDisabled={isLoading}
                            onPress={() => {
                              handleUnschedule()
                              close()
                            }}
                          >
                            Cancel Scheduled Posts
                          </Button>
                        )}
                        <Button
                          size="lg"
                          onPress={() => {
                            if (!calendarPickerPostAt) return

                            const scheduleStatus = hasIntegration
                              ? PostStatusPropertyValue.AutoPostScheduled
                              : PostStatusPropertyValue.ReminderScheduled
                            handleSchedule(new Date(calendarPickerPostAt), scheduleStatus)
                            close()
                          }}
                          isDisabled={isLoading || !targets?.length}
                        >
                          {almanacFile?.post_at ? 'Save New Date & Time' : 'Schedule Posts'}
                        </Button>
                      </div>
                    )}
                    setDateTime={(datetime) => {
                      if (datetime) setCalendarPickerPostAt(datetime.toISOString())
                    }}
                  >
                    <Button
                      variant={'tertiary'}
                      size="lg"
                      endIcon={ChevronDown}
                      isDisabled={isLoading || !targets?.length}
                      fullWidth
                    >
                      {currentAlmanacFile?.post_at
                        ? dayjs(currentAlmanacFile.post_at).format('ddd, MMM D h:mma z')
                        : 'Schedule Posts'}
                    </Button>
                  </DateTimePicker>
                </div>
              </Tooltip>
            )}
          </div>
        }
      />
      <Modal.Content>
        <div className={Styles.content}>
          {/* Posted Targets Section */}
          {postedTargets.length > 0 && (
            <Section title="Posted" columns={['Account', 'Post Date & Time', '']}>
              {postedTargets.map((target) => {
                const validation = validatePost(target.provider)

                return (
                  <ScheduledPostTargetRow
                    key={target.id}
                    target={target}
                    platform={target.provider}
                    account={getAccountForTarget(target)}
                    validation={validation}
                    isAlreadyPosted={true}
                    showPostDateTime={true}
                    onPublish={handlePublish}
                    onPublishWithoutTarget={() => handlePublishWithoutTarget(target.provider)}
                    onViewPost={handleViewPost}
                    onRetry={handleRetry}
                    postAt={currentAlmanacFile.post_at}
                    useActionButtons={true}
                    isEnabled={true}
                    onToggle={() => {}}
                    isAvailable={true}
                  />
                )
              })}
            </Section>
          )}

          {/* Failed Targets Section */}
          {failedTargets.length > 0 && (
            <Section
              title="Failed"
              columns={['Account', 'Post Date & Time', '']}
              backgroundColor="rgba(236, 30, 40, 0.10)"
            >
              {failedTargets.map((target) => {
                const validation = validatePost(target.provider)

                return (
                  <ScheduledPostTargetRow
                    key={target.id}
                    target={target}
                    platform={target.provider}
                    account={getAccountForTarget(target)}
                    validation={validation}
                    isAlreadyPosted={true}
                    showPostDateTime={true}
                    onPublish={handlePublish}
                    onPublishWithoutTarget={() => handlePublishWithoutTarget(target.provider)}
                    onViewPost={handleViewPost}
                    onRetry={handleRetry}
                    postAt={currentAlmanacFile.post_at}
                    useActionButtons={true}
                    isEnabled={true}
                    onToggle={() => {}}
                    isAvailable={true}
                  />
                )
              })}
            </Section>
          )}

          {/* Not Posted Section - only show for already published posts */}
          {status === PostStatusPropertyValue.Posted && (
            <Section title="Not Posted" columns={['Account', '', '']}>
              {crossPostGroup.map((platform) => {
                const target = getTargetForPlatform(platform)
                // Only show platforms that don't have posted/failed targets
                if (
                  postedTargets.some((t) => t.provider === platform) ||
                  failedTargets.some((t) => t.provider === platform)
                ) {
                  return null
                }

                const validation = validatePost(platform)

                return (
                  <ScheduledPostTargetRow
                    key={platform}
                    target={target}
                    platform={platform}
                    account={externalIntegrations?.external_integrations.find(
                      (integration) => integration.provider === platform,
                    )}
                    isEnabled={!!target}
                    onToggle={(isChecked) => {
                      handleToggle(platform, isChecked)
                    }}
                    isAvailable={
                      !!externalIntegrations?.external_integrations.some(
                        (integration) => integration.provider === platform,
                      )
                    }
                    validation={validation}
                    isAlreadyPosted={true}
                    onPublish={handlePublish}
                    onPublishWithoutTarget={() => handlePublishWithoutTarget(platform)}
                    onViewPost={handleViewPost}
                    onRetry={handleRetry}
                    useActionButtons={true}
                    showPostDateTime={true}
                  />
                )
              })}
            </Section>
          )}

          {[
            PostStatusPropertyValue.AutoPostScheduled,
            PostStatusPropertyValue.ReminderScheduled,
            PostStatusPropertyValue.Draft,
          ].includes(status) && (
            <Section
              title={status === PostStatusPropertyValue.AutoPostScheduled ? 'Scheduled Posts' : 'Schedule Posts'}
              columns={['Account', 'Enabled']}
            >
              {crossPostGroup.map((platform) => {
                const target = getTargetForPlatform(platform)
                // Only show platforms that don't have posted/failed targets
                if (
                  postedTargets.some((t) => t.provider === platform) ||
                  failedTargets.some((t) => t.provider === platform)
                ) {
                  return null
                }

                const validation = validatePost(platform)

                return (
                  <ScheduledPostTargetRow
                    key={platform}
                    target={target}
                    platform={platform}
                    account={externalIntegrations?.external_integrations.find(
                      (integration) => integration.provider === platform,
                    )}
                    isEnabled={!!target}
                    onToggle={(isChecked) => {
                      handleToggle(platform, isChecked)
                    }}
                    isAvailable={
                      !!externalIntegrations?.external_integrations.some(
                        (integration) => integration.provider === platform,
                      )
                    }
                    validation={validation}
                    isAlreadyPosted={false}
                    onPublish={handlePublish}
                    onPublishWithoutTarget={() => handlePublishWithoutTarget(platform)}
                    onViewPost={handleViewPost}
                    onRetry={handleRetry}
                    useActionButtons={true}
                    showPostDateTime={false}
                    isVideoCrossPost={showVideoCrossPost}
                  />
                )
              })}
            </Section>
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
